import { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { services, stats, testimonials, clients, methodologySteps, operturiFeatures, featuredServices } from '@/mocks/suviData';

function useScrollReveal(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin: '0px 0px -60px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

function RevealSection({ children, className = '', delay = 0, direction = 'up' }: { children: ReactNode; className?: string; delay?: number; direction?: 'up' | 'left' | 'right' | 'scale' }) {
  const { ref, visible } = useScrollReveal();

  const getInitialTransform = () => {
    switch (direction) {
      case 'left': return 'translateX(-60px)';
      case 'right': return 'translateX(60px)';
      case 'scale': return 'scale(0.92) translateY(20px)';
      default: return 'translateY(50px)';
    }
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0) translateX(0) scale(1)' : getInitialTransform(),
        transitionDelay: `${delay}ms`,
        transitionTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}
    >
      {children}
    </div>
  );
}

function AnimatedCounter({ target, suffix = '', duration = 2000 }: { target: number; suffix?: string; duration?: number }) {
  const [count, setCount] = useState(0);
  const { ref, visible } = useScrollReveal(0.3);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!visible || hasAnimated.current) return;
    hasAnimated.current = true;

    let start = 0;
    const end = target;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);

    return () => clearInterval(timer);
  }, [visible, target, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Duplicate clients for marquee
  const marqueeClients = [...clients, ...clients];

  return (
    <main className="min-h-screen overflow-hidden">
      {/* ========== HERO ========== */}
      <section className="relative min-h-[700px] md:min-h-[800px] flex items-center overflow-hidden">
        {/* Animated gradient background */}
        <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />

        {/* Floating blobs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200/40 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-200/30 rounded-full blur-3xl animate-float-delay-1" />
        <div className="absolute top-1/2 left-1/3 w-64 h-64 bg-accent-200/25 rounded-full blur-3xl animate-float-delay-2" />

        {/* Grid pattern overlay */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />

        {/* Hero Content */}
        <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
          <div className="max-w-4xl">
            <div
              className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm border border-primary-200/50 rounded-full px-4 py-2 mb-6"
              style={{ animation: 'fade-up 0.6s ease forwards', animationDelay: '0.2s', opacity: 0 }}
            >
              <span className="w-2 h-2 rounded-full bg-accent-500 animate-pulse" />
              <span className="text-primary-700 text-sm font-medium">Turismo + Tecnología · Marketing · IA · Software</span>
            </div>

            <h1
              className="font-heading text-4xl md:text-5xl lg:text-7xl font-bold text-foreground-900 leading-[1.1] mb-6"
              style={{ animation: 'fade-up 0.7s ease forwards', animationDelay: '0.4s', opacity: 0 }}
            >
              Impulsamos agencias de viaje con{' '}
              <span className="text-gradient">turismo + tecnología</span>
            </h1>

            <p
              className="text-foreground-600 text-lg md:text-xl leading-relaxed mb-8 max-w-2xl"
              style={{ animation: 'fade-up 0.7s ease forwards', animationDelay: '0.6s', opacity: 0 }}
            >
              Marketing digital, agentes de inteligencia artificial y software a medida.
              Automatizamos y optimizamos tus procesos para que crezcas de forma más rápida y efectiva.
            </p>

            <div
              className="flex flex-col sm:flex-row gap-4"
              style={{ animation: 'fade-up 0.7s ease forwards', animationDelay: '0.8s', opacity: 0 }}
            >
              <Link
                to="/contacto"
                className="btn-glow bg-primary-500 hover:bg-primary-600 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors whitespace-nowrap text-lg"
              >
                Asesoría Gratuita
              </Link>
              <Link
                to="/servicios"
                className="bg-white/70 hover:bg-white backdrop-blur-sm text-foreground-800 font-semibold px-8 py-4 rounded-xl text-center transition-all border border-primary-200/50 whitespace-nowrap text-lg"
              >
                Explorar Servicios
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <div className="w-6 h-10 border-2 border-primary-300 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-primary-400 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* ========== MARQUEE CLIENTS ========== */}
      <section className="bg-white py-6 border-y border-background-200/50 overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap">
          {marqueeClients.map((client, i) => (
            <div key={`${client.name}-${i}`} className="flex items-center gap-3 mx-8 md:mx-12 opacity-40 hover:opacity-70 transition-opacity">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-50 text-primary-400">
                <i className="ri-briefcase-line text-xs" />
              </div>
              <span className="font-heading font-medium text-foreground-600 text-sm">{client.name}</span>
              <span className="text-foreground-300 text-xs">·</span>
              <span className="text-foreground-400 text-xs">{client.type}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ========== STATS ========== */}
      <section className="bg-background-50 py-16 md:py-20">
        <div className="px-4 md:px-6 lg:px-10 xl:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, i) => {
              const num = parseInt(stat.value.replace(/\D/g, ''), 10);
              const suffix = stat.value.replace(/[0-9]/g, '');
              return (
                <RevealSection key={stat.label} delay={i * 150}>
                  <div className="text-center">
                    <p className="font-heading text-4xl md:text-5xl font-bold text-gradient mb-2">
                      <AnimatedCounter target={num} suffix={suffix} />
                    </p>
                    <p className="text-foreground-500 text-sm font-medium">{stat.label}</p>
                  </div>
                </RevealSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== SERVICES ========== */}
      <section className="bg-white py-20 md:py-28">
        <div className="px-4 md:px-6 lg:px-10 xl:px-16">
          <RevealSection>
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Nuestros Servicios
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Soluciones que{' '}
                <span className="text-gradient">impulsan</span>{' '}
                tu crecimiento
              </h2>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                Desde estrategia hasta ejecución, cubrimos todo el espectro digital para que tu agencia de viaje destaque.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {services.map((service, i) => (
              <RevealSection key={service.id} delay={i * 100}>
                <Link to="/servicios" className="group block h-full">
                  <div className="card-lift bg-background-50 border border-background-200/60 rounded-2xl p-6 h-full flex flex-col relative overflow-hidden">
                    {/* Hover gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-100/0 to-primary-100/0 group-hover:from-primary-100/60 group-hover:to-secondary-100/40 transition-all duration-500 rounded-2xl" />

                    <div className="relative z-10">
                      <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-600 mb-5 group-hover:scale-110 transition-transform duration-300">
                        <i className={`${service.icon} text-2xl`} />
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-foreground-900 mb-3 group-hover:text-primary-700 transition-colors">
                        {service.title}
                      </h3>
                      <p className="text-foreground-500 text-sm leading-relaxed flex-grow">
                        {service.shortDesc}
                      </p>
                      <span className="text-primary-600 text-sm font-semibold mt-4 inline-flex items-center gap-1.5 group-hover:gap-3 transition-all">
                        Saber más <i className="ri-arrow-right-line text-xs" />
                      </span>
                    </div>
                  </div>
                </Link>
              </RevealSection>
            ))}
          </div>

          <RevealSection delay={300}>
            <div className="text-center mt-12">
              <Link
                to="/servicios"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3.5 rounded-xl transition-all whitespace-nowrap card-lift"
              >
                Ver todos los servicios <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ========== FEATURED: AGENTES IA + SOFTWARE ========== */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-background-50 via-white to-secondary-50" />
        <div className="absolute top-20 left-20 w-64 h-64 bg-accent-100/30 rounded-full blur-3xl animate-float-slow" />
        <div className="absolute bottom-20 right-20 w-72 h-72 bg-primary-100/30 rounded-full blur-3xl animate-float-delay-1" />

        <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16">
          <RevealSection>
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-accent-100 text-accent-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-accent-200/50">
                Tecnología a tu favor
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Agentes IA y{' '}
                <span className="text-gradient">software a medida</span>
              </h2>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                Sabemos de turismo y de tecnología. Automatizamos tus procesos para que operes más rápido, efectivo y rentable.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {featuredServices.map((service, i) => (
              <RevealSection key={service.id} delay={i * 150} direction={i === 0 ? 'left' : 'right'}>
                <div className="relative h-full overflow-hidden rounded-3xl border border-primary-200/40 bg-white p-8 md:p-10 group">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-500 via-accent-400 to-secondary-500" />
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent-100/40 rounded-full blur-3xl group-hover:bg-primary-100/60 transition-colors duration-500" />

                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 text-white group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                        <i className={`${service.icon} text-2xl`} />
                      </div>
                      <span className="bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                        {service.tag}
                      </span>
                    </div>

                    <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-foreground-500 text-base leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="space-y-2.5 mb-8 flex-grow">
                      {service.features.slice(0, 4).map((feature) => (
                        <div key={feature} className="flex items-start gap-2.5 text-foreground-600 text-sm">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5 flex-shrink-0">
                            <i className="ri-check-line text-xs" />
                          </div>
                          {feature}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-background-200/60">
                      <div>
                        <p className="text-primary-700 font-heading font-bold text-lg">{service.pricing}</p>
                        <p className="text-foreground-400 text-xs mt-0.5">{service.duration}</p>
                      </div>
                      <Link
                        to="/servicios"
                        className="btn-glow bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl text-center transition-colors whitespace-nowrap"
                      >
                        Saber más
                      </Link>
                    </div>
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== OPERTURI ========== */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-800 via-primary-700 to-secondary-700" />

        {/* Floating elements */}
        <div className="absolute top-10 right-20 w-40 h-40 bg-white/5 rounded-full blur-2xl animate-float" />
        <div className="absolute bottom-10 left-20 w-56 h-56 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-1" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-float-delay-2" />

        <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text */}
            <RevealSection direction="left">
              <div>
                <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-5">
                  <i className="ri-sparkling-line text-accent-400 text-sm" />
                  <span className="text-white/80 text-sm font-medium">Software Propietario</span>
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                  Conoce{' '}
                  <span className="text-accent-400">Operturi</span>
                </h2>
                <p className="text-white/70 text-base md:text-lg leading-relaxed mb-6">
                  El software diseñado específicamente para agencias de viajes y tour operadores. Gestiona cotizaciones, itinerarios, reservas y clientes desde una sola plataforma intuitiva.
                </p>
                <p className="text-white/50 text-sm leading-relaxed mb-8">
                  Reduce el tiempo administrativo hasta en un 60%. Envía propuestas interactivas que tus clientes pueden aprobar online.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/operturi"
                    className="btn-glow bg-accent-500 hover:bg-accent-600 text-white font-semibold px-7 py-3.5 rounded-xl text-center transition-colors whitespace-nowrap"
                  >
                    Descubrir Operturi
                  </Link>
                  <a
                    href="https://wa.me/51989307864"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white/10 hover:bg-white/20 text-white font-semibold px-7 py-3.5 rounded-xl text-center transition-colors border border-white/25 whitespace-nowrap"
                  >
                    Solicitar Demo
                  </a>
                </div>
              </div>
            </RevealSection>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {operturiFeatures.map((feature, i) => (
                <RevealSection key={feature.title} delay={i * 120} direction="right">
                  <div className="bg-white/8 backdrop-blur-sm border border-white/10 hover:border-accent-400/40 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white/12">
                    <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-500/20 text-accent-400 mb-3">
                      <i className={`${feature.icon} text-lg`} />
                    </div>
                    <h4 className="font-heading font-semibold text-sm text-white mb-1.5">
                      {feature.title}
                    </h4>
                    <p className="text-white/50 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </RevealSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ========== METHODOLOGY ========== */}
      <section className="bg-background-50 py-20 md:py-28">
        <div className="px-4 md:px-6 lg:px-10 xl:px-16">
          <RevealSection>
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Nuestra Metodología
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Inbound Marketing que{' '}
                <span className="text-gradient">genera resultados</span>
              </h2>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                Atraemos prospectos, interactuamos a gran escala y deleitamos de manera individual.
              </p>
            </div>
          </RevealSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 relative">
            {/* Connector lines on desktop */}
            <div className="hidden md:block absolute top-1/2 left-[33%] right-[33%] h-0.5 bg-gradient-to-r from-primary-200 via-accent-200 to-secondary-200 -translate-y-1/2 z-0" />

            {methodologySteps.map((step, i) => (
              <RevealSection key={step.title} delay={i * 200}>
                <div className="relative bg-white border border-background-200/60 rounded-2xl p-6 md:p-8 z-10 h-full">
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-accent-100 text-primary-600 mb-5">
                    <i className={`${step.icon} text-2xl`} />
                  </div>
                  <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white text-xs font-bold mb-3">
                    {i + 1}
                  </div>
                  <h3 className="font-heading font-bold text-xl text-foreground-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-5">
                    {step.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {step.tools.map((tool) => (
                      <span
                        key={tool}
                        className="bg-background-50 text-foreground-600 text-xs font-medium px-3 py-1.5 rounded-full border border-background-200/50"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              </RevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* ========== TESTIMONIALS ========== */}
      <section className="bg-white py-20 md:py-28">
        <div className="px-4 md:px-6 lg:px-10 xl:px-16">
          <RevealSection>
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Testimonios
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900">
                Lo que dicen nuestros{' '}
                <span className="text-gradient">clientes</span>
              </h2>
            </div>
          </RevealSection>

          <RevealSection delay={100}>
            <div className="max-w-4xl mx-auto">
              <div className="relative bg-gradient-to-br from-background-50 to-white border border-background-200/60 rounded-2xl p-8 md:p-12 overflow-hidden">
                {/* Decorative quote */}
                <div className="absolute top-6 right-8 text-primary-100">
                  <i className="ri-double-quotes-r text-8xl" />
                </div>

                <div className="relative z-10">
                  <div className="flex items-start gap-1 mb-5">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <i key={star} className="ri-star-fill text-accent-500 text-base" />
                    ))}
                  </div>

                  <p className="text-foreground-700 text-lg md:text-xl leading-relaxed mb-8 font-light italic">
                    &ldquo;{testimonials[activeTestimonial].text}&rdquo;
                  </p>

                  <div className="flex items-center gap-4">
                    <img
                      src={testimonials[activeTestimonial].avatar}
                      alt={testimonials[activeTestimonial].name}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-primary-100"
                    />
                    <div>
                      <p className="font-heading font-semibold text-foreground-900 text-base">
                        {testimonials[activeTestimonial].name}
                      </p>
                      <p className="text-foreground-500 text-sm">
                        {testimonials[activeTestimonial].role}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Dots */}
              <div className="flex items-center justify-center gap-2 mt-8">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`h-2.5 rounded-full transition-all duration-300 ${
                      i === activeTestimonial ? 'w-8 bg-primary-500' : 'w-2.5 bg-background-300'
                    }`}
                    aria-label={`Ver testimonio ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </RevealSection>
        </div>
      </section>

      {/* ========== CTA ========== */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 animate-gradient bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600" />

        {/* Animated shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-[10%] w-64 h-64 bg-white/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-[15%] w-80 h-80 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-1" />
          <div className="absolute top-1/3 right-[30%] w-48 h-48 bg-white/5 rounded-full blur-2xl animate-float-delay-2" />
        </div>

        <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16">
          <RevealSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
                ¿Listo para transformar tu{' '}
                <span className="text-accent-300">presencia digital?</span>
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                Agenda tu primera asesoría gratuita de 30 minutos. Analizaremos tu situación actual y te daremos recomendaciones concretas para hacer crecer tu agencia de viajes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contacto"
                  className="btn-glow bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors whitespace-nowrap text-lg"
                >
                  Agendar Asesoría Gratis
                </Link>
                <a
                  href="https://wa.me/51989307864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/15 hover:bg-white/25 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors border border-white/30 whitespace-nowrap text-lg"
                >
                  <i className="ri-whatsapp-line mr-2" />
                  Escribir por WhatsApp
                </a>
              </div>
            </div>
          </RevealSection>
        </div>
      </section>
    </main>
  );
}